import React, { useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay";
import NumberFormat from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Loader from "../common/components/Loader";
import actions from "./actions";
import { useLocation, useHistory } from "react-router-dom";
import "../softCosts/softCosts.css";
import Portal from "../common/components/Portal";
import ConfirmationModal from "../common/components/ConfirmationModal";
import _, { isEqual } from "lodash";
import { checkPermission, findPrevPathFromBreadCrumpData, popBreadCrumpOnPageClose, showAlert as showAlertUtil } from "../../config/utils";
import { permissions } from "../../config/permissions";
import useSoftCostDynamicField from "./useSoftCostDynamicField";
import { EllipsisTooltipText } from "../common/components/EllipsisTextWithTooltip";
import commonActions from "../common/actions";
import ReactTooltip from "react-tooltip";
import { COLOR_PALETTE } from "./constants";
import { BulkDefaultsModal } from "./components/DefaultSettingsModal";
import { DatePopup } from "./components/DatePopup";
import { ERROR_COPY } from "../../config/errorMessages";

const SoftCosts = props => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { id, section, tab } = useParams();
    const { getSoftCosts, excelData } = useSelector(state => state.softCostReducer);
    const { isFormDirty: isDirty } = useSelector(state => state.commonReducer);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const projectId = queryParams.get("pid") || props.projectId;

    const [recommendations, setRecommendations] = useState([]);
    const [defaultDynamicSettings, setDefaultDynamicSettings] = useState({});

    const [initialData, setInitialData] = useState({
        recommendations: [],
        initialInflation: null,
        defaultDynamicSettings: {}
    });
    const [isLoading, setLoading] = useState(true);
    const [showConfirmModal, setConfirmationModal] = useState(false);
    const [showSaveConfirmation, setSaveConfirmation] = useState(false);
    const [isExporting, setExporting] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);
    const [yearSummary, setYearSummary] = useState({});
    const [showDefaultsModal, setShowDefaultsModal] = useState(false);
    const [confirmDefaults, setConfirmDefaults] = useState(false);
    const [confirmReset, setConfirmReset] = useState(false);
    const {
        dynamicSoftCosts,
        setDynamicSoftCosts,
        onDynamicFieldAdd,
        onDynamicFieldEdit,
        onDynamicFieldDelete,
        renderDynamicSoftCostModal,
        renderDeleteConfirmationModal,
        refreshToggle
    } = useSoftCostDynamicField({ props, id });

    useEffect(() => {
        setRecommendations([]);
        setDynamicSoftCosts([]);
        setInitialData({ recommendations: [] });
        setYearSummary({});
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, refreshToggle, tab, props.activeTab]);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [recommendations]);

    useEffect(() => {
        dispatch(commonActions.setFormDirty(_.isEqual(recommendations, initialData.recommendations) ? false : true));
    }, [recommendations, initialData.recommendations]);

    const getParams = () => {
        let params = {};
        if (section === "regioninfo") {
            params.region_id = id;
            params.project_id = projectId;
        } else if (section === "siteinfo") {
            params.site_id = id;
            params.project_id = projectId;
        } else if (section === "buildinginfo") {
            params.building_id = id;
            params.project_id = projectId;
        } else if (section === "leaseInfo") {
            params.lease_id = id;
            params.project_id = projectId;
        } else {
            params.project_id = id;
        }
        params.field_category = props.activeTab === "lease-income" ? "income" : "expense";
        return params;
    };
    const fetchData = async () => {
        let params = getParams();
        await dispatch(actions.getSoftCostsData(params));
        setLoading(false);
    };

    useEffect(() => {
        setSoftCostsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getSoftCosts]);

    useEffect(() => {
        if (!recommendations.length || !dynamicSoftCosts.length) return;
        const newYearSummary = recommendations.reduce((acc, item) => {
            acc[item.year] = calculateYearlySummary(item.year, recommendations);
            return acc;
        }, {});
        setYearSummary(newYearSummary);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recommendations, dynamicSoftCosts]);

    const setSoftCostsData = () => {
        const { lease_cost_data = [], year_totals = {}, dynamic_fields = [], default_actual = {} } = getSoftCosts || {};

        Object.keys(year_totals).forEach(year => {
            const idx = lease_cost_data.findIndex(obj => obj.year === year);
            if (idx !== -1) {
                lease_cost_data[idx].year_total = year_totals[year]?.value;
                lease_cost_data[idx].year_label = year_totals[year]?.label;
            } else {
                lease_cost_data.push({
                    year,
                    year_total: year_totals[year]?.value,
                    year_label: year_totals[year]?.label,
                    dynamic_fields: {},
                    actual_dynamic_fields: {}
                });
            }
        });

        const order = Object.keys(year_totals).reduce((acc, y, i) => {
            acc[y] = i;
            return acc;
        }, {});

        const PALETTE = typeof COLOR_PALETTE !== "undefined" && COLOR_PALETTE?.length ? COLOR_PALETTE : [{ light: "#e6f4ff", dark: "#1a73e8" }];

        const recom = lease_cost_data
            .filter(item => item.year && year_totals[item.year])
            .sort((a, b) => (order[a.year] ?? 9999) - (order[b.year] ?? 9999))
            .map((item, idx) => {
                const { light, dark } = PALETTE[idx % PALETTE.length];
                return {
                    year: item.year,
                    year_label: item.year_label ?? year_totals[item.year]?.label ?? "",
                    initialCsp: item.year_total == null ? null : Number(item.year_total),
                    dynamic_fields: item.dynamic_fields ?? {},
                    actual_dynamic_fields: item.actual_dynamic_fields ?? {},
                    light,
                    dark
                };
            });

        setDynamicSoftCosts(dynamic_fields.sort((a, b) => a.index - b.index));
        setRecommendations(recom);
        setDefaultDynamicSettings(default_actual);
        setInitialData({ recommendations: recom });
        setLoading(false);
    };

    const handleChangeSf = (year, value) => {
        if (!isNaN(value)) {
            const updatedRecom = recommendations.map(item => (item.year === year ? { ...item, initialCsp: value } : item));
            setRecommendations(updatedRecom);
        }
    };
    const handleChangeDynamicSc = (year, rate, key, inputType = "", section = "settings") => {
        if (!isNaN(rate)) {
            const updatedRecom = recommendations.map(item => {
                if (item.year !== year) return item;

                let dollar = "";
                let dollar_per_sf = "";

                if (inputType === "dollar") {
                    dollar = rate;
                    dollar_per_sf = item.initialCsp ? rate / item.initialCsp : "";
                } else if (inputType === "dollar_per_sf") {
                    dollar_per_sf = rate;
                    dollar = item.initialCsp ? rate * item.initialCsp : "";
                }

                const dynamicFieldKey = section === "actual" ? "actual_dynamic_fields" : "dynamic_fields";

                return {
                    ...item,
                    [dynamicFieldKey]: {
                        ...(item[dynamicFieldKey] || {}),
                        [key]: {
                            dollar,
                            dollar_per_sf
                        }
                    }
                };
            });

            setRecommendations(updatedRecom);
        }
    };

    const handleChangeActualDynamicSc = (year, rate, key) => {
        if (!isNaN(rate)) {
            const updatedRecom = recommendations.map(item =>
                item.year === year ? { ...item, actual_dynamic_fields: { ...item.actual_dynamic_fields, [key]: rate } } : item
            );
            setRecommendations(updatedRecom);
        }
    };

    const getPercentage = (amount = 0, percentage = 0) => {
        const factor = percentage / 100;
        const percAmount = amount * factor;
        return percAmount;
    };

    const setCompoundInflationForAll = percentage => {
        const percFactor = percentage / 100;
        let prevInflation = 1;
        const updated = recommendations.map((item, idx) => {
            const newInflation = idx === 0 ? 1 : prevInflation + percFactor * prevInflation;
            prevInflation = newInflation;
            return { ...item, inflation: idx === 0 ? 0 : percentage, c_inflation: newInflation };
        });
        return updated;
    };

    const getSoftCostTotal = (type, recom, input) => {
        const sourceField = type === "settings" ? "dynamic_fields" : "actual_dynamic_fields";
        return dynamicSoftCosts.reduce((total, { key }) => {
            const value = parseFloat(recom?.[sourceField]?.[key]?.[input]) || 0;
            return total + value;
        }, 0);
    };

    const getTotalCosts = ({ key, type, dynamicFieldKey }) => {
        let sum = 0;
        if (!recommendations.length) return sum;
        let values = [];
        if (key === "initialCsp") {
            values = recommendations.map(item => item?.initialCsp);
        } else if (key === "dynamicField") {
            values = recommendations.map(item => item?.[type]?.[dynamicFieldKey]?.dollar || 0);
        } else {
            values = Object.keys(yearSummary).map(year => yearSummary?.[year]?.[key]?.[type]?.amount || 0);
        }
        sum = values.reduce((acc, curr) => {
            return acc + curr;
        }, 0);
        return sum;
    };

    const saveData = async () => {
        setSubmitting(true);

        const { changedItems: changedSoftCosts, dynamic_field_present } = getChangedData(recommendations, initialData.recommendations);
        const hasDynamicChanged = !isEqual(defaultDynamicSettings, initialData.defaultDynamicSettings);
        const shouldSave = changedSoftCosts.length > 0 || hasDynamicChanged;

        if (shouldSave) {
            let data = {
                ...(changedSoftCosts.length > 0 && {
                    leases_costs: changedSoftCosts,
                    dynamic_field_present
                }),
                ...(hasDynamicChanged && {
                    default_actual: defaultDynamicSettings
                }),
                ...getParams()
            };
            if (data.lease_id) {
                data.leases_id = data.lease_id;
                delete data.lease_id;
            }

            const result = dispatch(actions.saveSoftCostsData(data));
            if (result && typeof result.then === "function") {
                result
                    .then(() => {
                        setSubmitting(false);
                        fetchData();
                        showAlerts("Saved Successfully");
                    })
                    .catch(() => {
                        setSubmitting(false);
                        showAlerts(ERROR_COPY.GENERIC);
                    });
            } else {
                setSubmitting(false);
            }
        } else {
            setSubmitting(false);
            showAlerts("No changes detected");
        }
    };

    const dynamicFieldTypeByKey = (dynamicSoftCosts || []).reduce((acc, { key, field_type }) => {
        acc[key] = field_type;
        return acc;
    }, {});

    const normalizeDynamicValueForBackend = (dynamicKey, rawValue) => {
        const t = dynamicFieldTypeByKey[dynamicKey];
        const dollar = rawValue?.dollar ?? "";
        const dollar_per_sf = rawValue?.dollar_per_sf ?? "";

        if (t === "dollar") {
            return { dollar, dollar_per_sf: "" };
        }
        if (t === "dollar_per_sf") {
            return { dollar: "", dollar_per_sf };
        }
        return { dollar, dollar_per_sf };
    };

    const getChangedData = (currentData, initialData) => {
        let dynamicFieldPresent = false;

        const changedItems = currentData.reduce((changedItems, currentItem) => {
            const initialItem = initialData.find(item => item.year === currentItem.year);
            let changedItem = { year: currentItem.year };

            Object.keys(currentItem).forEach(key => {
                if (key === "year") return;

                if (key === "dynamic_fields" || key === "actual_dynamic_fields") {
                    const dynamicChanges = {};

                    Object.keys(currentItem[key] || {}).forEach(dynamicKey => {
                        const curr = currentItem[key][dynamicKey];
                        const prev = (initialItem[key] || {})[dynamicKey];

                        const isDifferent =
                            (curr?.dollar ?? "") !== (prev?.dollar ?? "") || (curr?.dollar_per_sf ?? "") !== (prev?.dollar_per_sf ?? "");

                        if (isDifferent) {
                            dynamicChanges[dynamicKey] = normalizeDynamicValueForBackend(dynamicKey, curr);
                        }
                    });

                    if (Object.keys(dynamicChanges).length > 0) {
                        changedItem[key] = dynamicChanges;
                        dynamicFieldPresent = true;
                    }
                }
            });

            if (Object.keys(changedItem).length > 1) {
                changedItems.push(changedItem);
            }

            return changedItems;
        }, []);

        return {
            changedItems,
            dynamic_field_present: dynamicFieldPresent
        };
    };

    const disableInflationInputs = false;

    const showAlerts = msg => showAlertUtil(msg);

    const onCancel = () => {
        setRecommendations(initialData.recommendations);
    };

    const renderConfirmationModal = () => {
        if (!showConfirmModal) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        type="cancel"
                        heading={"Do you want to discard all changes?"}
                        message={"This action cannot be reverted, are you sure that you need to discard changes?"}
                        onNo={() => setConfirmationModal(false)}
                        onYes={() => {
                            setConfirmationModal(false);
                            onCancel();
                        }}
                    />
                }
                onCancel={() => setConfirmationModal(false)}
            />
        );
    };

    const renderSaveConfirmationModal = () => {
        if (!showSaveConfirmation) return null;
        const msg = getConfirmationMessage();
        return (
            <Portal
                body={
                    <ConfirmationModal
                        type="cancel"
                        heading={msg}
                        message={"Do you want to proceed?"}
                        onNo={() => setSaveConfirmation(false)}
                        onYes={() => {
                            setSaveConfirmation(false);
                            saveData();
                        }}
                    />
                }
                onCancel={() => setSaveConfirmation(false)}
            />
        );
    };

    const getConfirmationMessage = () => {
        switch (section) {
            case "projectinfo":
                return `You are about to save changes at the Project level. 
                        This will apply the new values to all regions, sites, and buildings under this project, 
                        overwriting any existing values at those levels.`;

            case "regioninfo":
                return `You are updating values at the Region level. 
                        The changes will apply to all sites and buildings under this region, 
                        and the Project level will receive a calculated value based on the updated regions.`;

            case "siteinfo":
                return `You are making changes at the Site level. 
                        The updated values will apply to all buildings within this site, 
                        and calculated values will update at both the Region and Project levels.`;

            case "buildinginfo":
                return `You are updating values at the Building level. 
                        This change will trigger recalculated values for the Site, Region, and Project levels.`;
            case "leaseInfo":
                return `You are updating values at the Lease level. 
                        This change will trigger a recalculation of values upstream for the Building, Site, Region, and Project levels.`;

            default:
                return "Unknown section. Please select a valid section to save changes.";
        }
    };

    const exportExcel = async () => {
        let params = getParams();
        try {
            setExporting(true);
            await dispatch(actions.exportExcel(params));
            setExporting(false);
        } catch (error) {
            console.log(error);
            showAlerts("Export Failed");
        }
    };

    const calculatePercentage = (value = 0, base = 0) => {
        if (base === 0 || isNaN(value) || isNaN(base)) {
            return 0;
        }
        let percentage = (value / base) * 100;
        return parseFloat(percentage.toString());
    };

    const calculateYearlySummary = (year, updatedRecom) => {
        const recom = updatedRecom.find(item => item.year === year);

        const softCostTotal = {
            settings: {
                percentage: getSoftCostTotal("settings", recom, "dollar_per_sf") || 0,
                amount: getSoftCostTotal("settings", recom, "dollar") || 0
            },
            actual: {
                percentage: getSoftCostTotal("actual", recom, "dollar_per_sf") || 0,
                amount: getSoftCostTotal("actual", recom, "dollar") || 0
            }
        };

        return {
            softCostTotal
        };
    };

    const getSectionWithName = () => {
        const { name = "", code = "" } = props.basicDetails || {};
        let scSection = "";
        let sectionName = "";

        switch (section) {
            case "projectinfo":
                scSection = "Project";
                sectionName = name;
                break;
            case "regioninfo":
                scSection = "Region";
                sectionName = name;
                break;
            case "siteinfo":
                scSection = "Site";
                sectionName = name;
                break;
            case "buildinginfo":
                scSection = "Building";
                sectionName = name;
                break;
            case "recommendationsinfo":
                scSection = "Recommendation";
                sectionName = code;
                break;
            default:
                break;
        }

        return (
            <>
                {`FCA CSP Total Costs > `}
                {scSection}
                {sectionName && ` > `}
                {sectionName && <span className="border-active active-col">{sectionName}</span>}
            </>
        );
    };

    const handleRefresh = () => {
        setLoading(true);
        fetchData();
    };

    const renderResetAllConfirmationModal = () => {
        if (!confirmReset) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        type="cancel"
                        heading="Do you want to reset all data?"
                        message={
                            <>
                                <p>
                                    You’re about to reset all lease {props.activeTab === "lease-income" ? "Income" : "Expense"} cost data for this
                                    project.
                                </p>
                                <p>
                                    This will permanently delete <strong>Settings and Actual</strong> data for every region, site, building, and
                                    leases.
                                </p>
                                <p>This action cannot be undone.</p>
                                <p>Proceed?</p>
                            </>
                        }
                        onNo={() => setConfirmReset(false)}
                        onYes={() => {
                            setConfirmReset(false);
                            handleResetAll();
                        }}
                    />
                }
                onCancel={() => setConfirmReset(false)}
            />
        );
    };

    const handleResetAll = () => {
        setLoading(true);
        const result = dispatch(actions.resetSoftCost({ project_id: id, field_category: props.activeTab === "lease-income" ? "income" : "expense" }));
        if (result && typeof result.then === "function") {
            result
                .then(() => {
                    showAlerts(`Lease cost reset completed successfully.`);
                    fetchData();
                })
                .catch(() => {
                    setLoading(false);
                    showAlerts(ERROR_COPY.GENERIC);
                });
        } else {
            setLoading(false);
        }
    };

    const openDefaults = () => setShowDefaultsModal(true);
    const closeDefaults = () => setShowDefaultsModal(false);

    const handleDefaultsSave = vals => {
        bulkValuesRef.current = vals;
        setConfirmDefaults(true);
    };
    const bulkValuesRef = React.useRef({});

    const onConfirmDefaults = () => {
        const { dynamic_fields: dyn } = bulkValuesRef.current;
        if (settingsFieldDisabled) {
            let updated = recommendations.map(item => ({
                ...item,
                actual_dynamic_fields: Object.entries(dyn).reduce((acc, [key, value]) => {
                    const t = dynamicFieldTypeByKey[key];
                    if ((value || value === 0) && !isNaN(value)) {
                        if (t === "dollar") {
                            acc[key] = {
                                dollar: value,
                                dollar_per_sf: item.initialCsp ? value / item.initialCsp : ""
                            };
                        }

                        if (t === "dollar_per_sf") {
                            acc[key] = {
                                dollar: item.initialCsp ? value * item.initialCsp : "",
                                dollar_per_sf: value
                            };
                        }
                    } else {
                        acc[key] = item.actual_dynamic_fields[key] || {};
                    }

                    return acc;
                }, {})
            }));

            setDefaultDynamicSettings(dyn);
            setRecommendations(updated);
        }

        closeDefaults();
        setConfirmDefaults(false);
    };

    const hasEdit = checkPermission("forms", permissions.LEASE_COST, "edit");
    const settingsFieldDisabled = true;
    const actualFieldDisabled = section !== "leaseInfo" || !hasEdit;
    const hasExport = false;
    // const hasExport = checkPermission("forms", permissions.SOFT_COSTS, "export");
    const isSuperAdmin = localStorage.getItem("role") === "super_admin";
    return (
        <LoadingOverlay active={getSoftCosts?.loading || isLoading} spinner={<Loader />} fadeSpeed={10}>
            <div
                className={`tab-active location-sec image-sec tab-grey efci-otr fund-efci pb-0 ${
                    tab === "buildingLease" ? "soft-cost-lease" : "inner-soft-cost-lease"
                }`}
            >
                {renderConfirmationModal()}
                {renderSaveConfirmationModal()}
                {renderDynamicSoftCostModal()}
                {renderDeleteConfirmationModal()}
                {renderResetAllConfirmationModal()}
                {showDefaultsModal && (
                    <Portal
                        body={
                            <BulkDefaultsModal
                                onCancel={closeDefaults}
                                onSave={handleDefaultsSave}
                                initialValues={{
                                    dynamic_fields: defaultDynamicSettings
                                }}
                                dynamicFields={dynamicSoftCosts}
                                settingsFieldDisabled={settingsFieldDisabled}
                            />
                        }
                        onCancel={closeDefaults}
                    />
                )}

                {confirmDefaults && (
                    <Portal
                        body={
                            <ConfirmationModal
                                type="confirm"
                                heading={`Apply default ${settingsFieldDisabled ? "actual" : "settings"} to all years?`}
                                message={
                                    <>
                                        <p>This will overwrite all existing {settingsFieldDisabled ? "actual" : "setting"} values for every year.</p>
                                        <p>{settingsFieldDisabled ? "Settings will remain unchanged." : "Actuals will remain unchanged."}</p>
                                        {!settingsFieldDisabled && section !== "buildinginfo" && (
                                            <p>
                                                This will also change default settings of{" "}
                                                {section === "projectinfo"
                                                    ? "region, site, and building"
                                                    : section === "regioninfo"
                                                    ? "site and building"
                                                    : section === "siteinfo"
                                                    ? "building"
                                                    : null}
                                                .
                                            </p>
                                        )}
                                        <p>Proceed?</p>
                                    </>
                                }
                                onNo={() => setConfirmDefaults(false)}
                                onYes={onConfirmDefaults}
                            />
                        }
                        onCancel={() => setConfirmDefaults(false)}
                    />
                )}

                <div className={`dtl-sec col-md-12 mt-0`}>
                    <div className="head-wrapper d-flex">
                        <div className="otr-topr d-flex">
                            {/* <h3>{getSectionWithName()}</h3> */}
                            {hasEdit && !actualFieldDisabled && dynamicSoftCosts?.length > 0 ? (
                                <div className="soft-btnotr">
                                    <button className="soft-btn" onClick={openDefaults}>
                                        <i class="fas fa-sliders-h"></i>Default {settingsFieldDisabled ? "Actual" : "Settings"}
                                    </button>
                                </div>
                            ) : null}

                            <div className="soft-btnotr">
                                <button className="soft-btn" onClick={handleRefresh}>
                                    <i class="fas fa-sync"></i> Refresh
                                </button>
                            </div>
                            {section === "projectinfo" && isSuperAdmin && (
                                <div className="soft-btnotr reset-btn">
                                    <button className="soft-btn " onClick={() => setConfirmReset(true)}>
                                        <i class="fas fa-trash"></i>Reset All
                                    </button>
                                </div>
                            )}

                            {hasEdit && (
                                <div className="soft-btnotr">
                                    <button className="soft-btn" onClick={() => onDynamicFieldAdd()}>
                                        <i class="fas fa-plus"></i> Add {props.activeTab === "lease-income" ? "Income" : "Expense"}
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="filter-sec-plan d-flex pr-0 align-items-center">
                            {hasExport && (
                                <div className="soft-cost-excel">
                                    {isExporting ? (
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    ) : (
                                        <div
                                            className="view-inner"
                                            onClick={() => exportExcel()}
                                            data-for="table-top-icons"
                                            data-tip="Export to Excel"
                                        >
                                            <img src="/img/excell-new.svg" alt="" className="export" />
                                        </div>
                                    )}
                                </div>
                            )}

                            {hasEdit && (
                                <div className="add-btn-wrapper pt-0">
                                    <button
                                        className={`button btn-discard ml-2 ${!isDirty ? "disbled-butn" : ""}`}
                                        disabled={!isDirty || isSubmitting}
                                        onClick={() => setConfirmationModal(true)}
                                    >
                                        Discard Changes
                                    </button>
                                    <button
                                        className={`button btn-save ml-2 cursor-hand ${!isDirty ? "disbled-butn" : ""}`}
                                        disabled={!isDirty}
                                        onClick={() => setSaveConfirmation(true)}
                                    >
                                        Save
                                        {isSubmitting ? <span className="spinner-border spinner-border-sm pl-2 ml-1" role="status"></span> : null}
                                    </button>
                                </div>
                            )}
                            {section === "leaseInfo" && (
                                <div className="edit-icn-container otr-edit-delte pr-0">
                                    <span
                                        class="edit-icn-bx"
                                        onClick={() => {
                                            popBreadCrumpOnPageClose();
                                            history.push(findPrevPathFromBreadCrumpData());
                                        }}
                                    >
                                        <i className="fas fa-window-close"></i> Close
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                    <ReactTooltip
                        id="projectCost"
                        className="rc-tooltip-custom-class"
                        backgroundColor="#007bff"
                        place="left"
                        effect="solid"
                        delayShow={500}
                    />
                    {/* add extra div 'custom-table-scroll' for scroll issue */}
                    <div className="custom-table-scroll">
                        <div className="table-section table-plan table-scroll build-fci fc-capital-nw build-efci proj-soft-cost">
                            <table className="table table-common-outer">
                                <tbody>
                                    <tr>
                                        <td>
                                            <table className="table table-common table-froze-soft proj-soft-cost">
                                                <thead>
                                                    <tr className="table-head-title table-row-sf">
                                                        <th className="img-sq-box">
                                                            <img src="/img/sq-box.png" alt="" />
                                                        </th>
                                                        <th className="build-add title-soft" typeof="input">
                                                            Title
                                                        </th>
                                                        {recommendations.map(item => (
                                                            <th
                                                                key={item.year}
                                                                className="soft-cost-cell gap-x text-center"
                                                                style={{ backgroundColor: item.dark }}
                                                            >
                                                                {item.year_label}
                                                            </th>
                                                        ))}
                                                        <th className="soft-cost-cell gap-x text-center">Total SF</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className="blue-highlight-row brd-gap">
                                                        <th className="img-sq-box">
                                                            <img src="/img/sq-box.png" alt="" />
                                                        </th>
                                                        <th className="build-add title-soft">{section === "leaseInfo" ? "SF" : ""}</th>
                                                        {recommendations.map(item => (
                                                            <th key={item.year} className="build-add gap-x text-center dark-txt">
                                                                {section === "leaseInfo" && (
                                                                    <NumberFormat
                                                                        displayType="text"
                                                                        value={item.initialCsp}
                                                                        suffix=" SF"
                                                                        thousandSeparator={true}
                                                                        onValueChange={value => handleChangeSf(item.year, value.floatValue ?? "")}
                                                                    />
                                                                )}
                                                            </th>
                                                        ))}
                                                        <th className="build-add blue-bg-col gap-x text-center dark-txt">
                                                            {section === "leaseInfo" && (
                                                                <NumberFormat
                                                                    displayType="text"
                                                                    value={getTotalCosts({ key: "initialCsp" })}
                                                                    decimalScale={1}
                                                                    suffix=" SF"
                                                                    thousandSeparator={true}
                                                                />
                                                            )}
                                                        </th>
                                                    </tr>

                                                    <tr className="tb-row soft-cost-tr">
                                                        <th className="img-sq-box">
                                                            <img src="/img/sq-box.png" alt="" />
                                                        </th>
                                                        <th className="build-add soft-cost-title title-soft">
                                                            {props.activeTab === "lease-income" ? "Income" : "Expense"}
                                                        </th>
                                                        {recommendations.map(item => (
                                                            <>
                                                                <th key={`${item.year}-settings`} className="build-add value-pad gap-x">
                                                                    <div className="d-flex setting-actual-outer col-md-12">
                                                                        <div className="col-md-6 txt-justify-center">Setting</div>
                                                                        <div className="col-md-6 txt-justify-center">Actual</div>
                                                                    </div>
                                                                </th>
                                                            </>
                                                        ))}
                                                        <th key={`action`} className="build-add value-pad sticky-th-top gap-x">
                                                            <div className="d-flex setting-actual-outer col-md-12">
                                                                <div className="col-md-6 txt-justify-center">Setting</div>
                                                                <div className="col-md-6 txt-justify-center">Actual</div>
                                                            </div>
                                                        </th>
                                                    </tr>
                                                    {dynamicSoftCosts.map((sc, idx) => (
                                                        <tr>
                                                            <th className="img-sq-box">
                                                                <img src="/img/sq-box.png" alt="" />
                                                            </th>
                                                            <th className="build-add wide-add">
                                                                <div className="outer-build-section d-flex">
                                                                    <EllipsisTooltipText className="sc-name" text={sc.name} tooltipPlace="right" />
                                                                    {sc.color_code && (
                                                                        <span
                                                                            style={{
                                                                                backgroundColor: sc.color_code,
                                                                                width: "18px",
                                                                                height: "15px",
                                                                                borderRadius: "50%",
                                                                                display: "inline-block",
                                                                                marginLeft: "6px"
                                                                            }}
                                                                            title={`Color: ${sc.color_code}`}
                                                                        ></span>
                                                                    )}

                                                                    {/* {sc.inputType === "dollar" && (
                                                                        <i
                                                                            className="fas fa-dollar-sign text-muted"
                                                                            title="Flat Dollar Value"
                                                                        ></i>
                                                                    )}
                                                                    {sc.inputType === "dollar_per_sf" && (
                                                                        <i
                                                                            className="fas fa-ruler-combined text-muted"
                                                                            title="Per Square Foot"
                                                                        ></i>
                                                                    )} */}
                                                                    {!disableInflationInputs && hasEdit && (
                                                                        <span className="action-menu ml-2">
                                                                            <span className="outer-menu-action">
                                                                                <i class="fas fa-pencil-alt" onClick={() => onDynamicFieldEdit(sc)}>
                                                                                    {" "}
                                                                                </i>
                                                                                {"  "}
                                                                                <i class="fas fa-trash-alt" onClick={() => onDynamicFieldDelete(sc)}>
                                                                                    {" "}
                                                                                </i>
                                                                            </span>
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </th>
                                                            {recommendations.map(item => (
                                                                <>
                                                                    <th
                                                                        key={`${item.year}-${sc.key}-settings-input`}
                                                                        className="build-add set-value gap-x"
                                                                    >
                                                                        <div className="d-flex col-md-12 per-value-outer">
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value">
                                                                                    <NumberFormat
                                                                                        disabled={settingsFieldDisabled}
                                                                                        className={`build-add ${
                                                                                            settingsFieldDisabled ? "cursor-disabled" : ""
                                                                                        }`}
                                                                                        displayType={sc.field_type === "dollar" ? "text" : "input"}
                                                                                        allowNegative={false}
                                                                                        value={item.dynamic_fields?.[sc.key]?.dollar_per_sf ?? ""}
                                                                                        onValueChange={(value, sourceInfo) => {
                                                                                            if (sourceInfo?.source === "event") {
                                                                                                handleChangeDynamicSc(
                                                                                                    item.year,
                                                                                                    value.floatValue ?? "",
                                                                                                    sc.key,
                                                                                                    "dollar_per_sf",
                                                                                                    "settings"
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                        suffix="/SF"
                                                                                        prefix="$ "
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={2}
                                                                                    />
                                                                                </div>
                                                                                <div className="text-number">
                                                                                    <NumberFormat
                                                                                        disabled={settingsFieldDisabled}
                                                                                        className={`build-add ${
                                                                                            settingsFieldDisabled ? "cursor-disabled" : ""
                                                                                        }`}
                                                                                        thousandSeparator={true}
                                                                                        displayType={sc.field_type === "dollar" ? "input" : "text"}
                                                                                        value={item?.dynamic_fields?.[sc.key]?.dollar ?? ""}
                                                                                        prefix="$ "
                                                                                        decimalScale={1}
                                                                                        onValueChange={(value, sourceInfo) => {
                                                                                            if (sourceInfo?.source === "event") {
                                                                                                handleChangeDynamicSc(
                                                                                                    item.year,
                                                                                                    value.floatValue ?? "",
                                                                                                    sc.key,
                                                                                                    "dollar",
                                                                                                    "settings"
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            <div className="d-flex col-md-6 align-items-center">
                                                                                <div className="percentage-value actual_lease_value">
                                                                                    <NumberFormat
                                                                                        disabled={actualFieldDisabled}
                                                                                        className={`build-add ${
                                                                                            actualFieldDisabled ? "cursor-disabled" : ""
                                                                                        }`}
                                                                                        displayType={sc.field_type === "dollar" ? "text" : "input"}
                                                                                        allowNegative={false}
                                                                                        value={
                                                                                            item.actual_dynamic_fields?.[sc.key]?.dollar_per_sf ?? ""
                                                                                        }
                                                                                        onValueChange={(value, sourceInfo) => {
                                                                                            if (sourceInfo?.source === "event") {
                                                                                                handleChangeDynamicSc(
                                                                                                    item.year,
                                                                                                    value.floatValue ?? "",
                                                                                                    sc.key,
                                                                                                    "dollar_per_sf",
                                                                                                    "actual"
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                        suffix="/SF"
                                                                                        prefix="$ "
                                                                                        allowEmptyFormatting={true}
                                                                                        decimalScale={2}
                                                                                        data-tip={
                                                                                            new Intl.NumberFormat("en-US", {
                                                                                                style: "currency",
                                                                                                currency: "USD",
                                                                                                minimumFractionDigits: 0,
                                                                                                maximumFractionDigits: 1
                                                                                            }).format(
                                                                                                item.actual_dynamic_fields?.[sc.key]?.dollar_per_sf ??
                                                                                                    ""
                                                                                            ) + "/SF"
                                                                                        }
                                                                                        data-for="actualCost"
                                                                                    />
                                                                                    {section === "leaseInfo" &&
                                                                                    item.actual_dynamic_fields?.[sc.key]?.dollar_per_sf ? (
                                                                                        <DatePopup
                                                                                            dateRange={item.year_label}
                                                                                            dollar={
                                                                                                item.actual_dynamic_fields?.[sc.key]?.dollar_per_sf ??
                                                                                                0
                                                                                            }
                                                                                        />
                                                                                    ) : null}
                                                                                </div>
                                                                                <div className="text-number actual_lease_value">
                                                                                    <NumberFormat
                                                                                        disabled={actualFieldDisabled}
                                                                                        className={`build-add ${
                                                                                            actualFieldDisabled ? "cursor-disabled" : ""
                                                                                        }`}
                                                                                        thousandSeparator={true}
                                                                                        displayType={sc.field_type === "dollar" ? "input" : "text"}
                                                                                        value={item?.actual_dynamic_fields?.[sc.key]?.dollar ?? ""}
                                                                                        prefix="$ "
                                                                                        decimalScale={1}
                                                                                        onValueChange={(value, sourceInfo) => {
                                                                                            if (sourceInfo?.source === "event") {
                                                                                                handleChangeDynamicSc(
                                                                                                    item.year,
                                                                                                    value.floatValue ?? "",
                                                                                                    sc.key,
                                                                                                    "dollar",
                                                                                                    "actual"
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                        data-for="actualCost"
                                                                                        data-tip={new Intl.NumberFormat("en-US", {
                                                                                            style: "currency",
                                                                                            currency: "USD",
                                                                                            minimumFractionDigits: 0,
                                                                                            maximumFractionDigits: 1
                                                                                        }).format(
                                                                                            item?.actual_dynamic_fields?.[sc.key]?.dollar ?? ""
                                                                                        )}
                                                                                    />
                                                                                    {section === "leaseInfo" &&
                                                                                    item.actual_dynamic_fields?.[sc.key]?.dollar ? (
                                                                                        <DatePopup
                                                                                            dateRange={item.year_label}
                                                                                            dollar={item.actual_dynamic_fields?.[sc.key]?.dollar ?? 0}
                                                                                        />
                                                                                    ) : null}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </th>
                                                                </>
                                                            ))}
                                                            <th className="build-add set-value gap-x">
                                                                <div className="d-flex col-md-12 per-value-outer">
                                                                    <div className="d-flex col-md-6 align-items-center">
                                                                        <div className="text-number">
                                                                            <NumberFormat
                                                                                displayType="text"
                                                                                value={getTotalCosts({
                                                                                    key: "dynamicField",
                                                                                    type: "dynamic_fields",
                                                                                    dynamicFieldKey: sc.key
                                                                                })}
                                                                                decimalScale={0}
                                                                                prefix={"$ "}
                                                                                thousandSeparator={true}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex col-md-6 align-items-center">
                                                                        <div className="text-number">
                                                                            <NumberFormat
                                                                                displayType="text"
                                                                                value={getTotalCosts({
                                                                                    key: "dynamicField",
                                                                                    type: "actual_dynamic_fields",
                                                                                    dynamicFieldKey: sc.key
                                                                                })}
                                                                                decimalScale={0}
                                                                                prefix={"$ "}
                                                                                thousandSeparator={true}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </th>
                                                        </tr>
                                                    ))}
                                                    <tr className="blue-tot-bg-row">
                                                        <th className="img-sq-box">
                                                            <img src="/img/sq-box.png" alt="" />
                                                        </th>
                                                        <th className="build-add">Lease Cost Total</th>
                                                        {recommendations.map(item => (
                                                            <>
                                                                <th
                                                                    key={`${item.year}-soft-cost-total-settings-input`}
                                                                    className="build-add set-value gap-x"
                                                                    style={{ backgroundColor: item.light }}
                                                                >
                                                                    <div className="d-flex col-md-12 per-value-outer">
                                                                        <div className="d-flex col-md-6 align-items-center">
                                                                            <div className="percentage-value">
                                                                                <NumberFormat
                                                                                    className="build-add cursor-disabled"
                                                                                    displayType="text"
                                                                                    allowNegative={false}
                                                                                    value={
                                                                                        yearSummary?.[item.year]?.softCostTotal?.settings?.percentage
                                                                                    }
                                                                                    suffix="/SF"
                                                                                    prefix="$ "
                                                                                    allowEmptyFormatting={true}
                                                                                    decimalScale={1}
                                                                                />
                                                                            </div>
                                                                            <div className="text-number">
                                                                                <NumberFormat
                                                                                    className="build-add cursor-disabled"
                                                                                    thousandSeparator={true}
                                                                                    displayType="text"
                                                                                    value={yearSummary?.[item.year]?.softCostTotal?.settings?.amount}
                                                                                    prefix="$ "
                                                                                    decimalScale={0}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <div className="d-flex col-md-6 align-items-center">
                                                                            <div className="percentage-value actual_lease_value">
                                                                                <NumberFormat
                                                                                    className="build-add cursor-disabled"
                                                                                    displayType="text"
                                                                                    allowNegative={false}
                                                                                    value={
                                                                                        yearSummary?.[item.year]?.softCostTotal?.actual?.percentage
                                                                                    }
                                                                                    suffix="/SF"
                                                                                    prefix="$ "
                                                                                    allowEmptyFormatting={true}
                                                                                    decimalScale={1}
                                                                                    data-for="actualCost"
                                                                                    data-tip={
                                                                                        new Intl.NumberFormat("en-US", {
                                                                                            style: "currency",
                                                                                            currency: "USD",
                                                                                            minimumFractionDigits: 0,
                                                                                            maximumFractionDigits: 1
                                                                                        }).format(
                                                                                            yearSummary?.[item.year]?.softCostTotal?.actual
                                                                                                ?.percentage ?? ""
                                                                                        ) + "/SF"
                                                                                    }
                                                                                />
                                                                            </div>
                                                                            <div className="text-number actual_lease_value">
                                                                                <NumberFormat
                                                                                    className="build-add cursor-disabled"
                                                                                    thousandSeparator={true}
                                                                                    displayType="text"
                                                                                    value={yearSummary?.[item.year]?.softCostTotal?.actual?.amount}
                                                                                    prefix="$ "
                                                                                    decimalScale={0}
                                                                                    data-for="actualCost"
                                                                                    data-tip={new Intl.NumberFormat("en-US", {
                                                                                        style: "currency",
                                                                                        currency: "USD",
                                                                                        minimumFractionDigits: 0,
                                                                                        maximumFractionDigits: 0
                                                                                    }).format(
                                                                                        yearSummary?.[item.year]?.softCostTotal?.actual?.amount ?? ""
                                                                                    )}
                                                                                />
                                                                            </div>
                                                                            <ReactTooltip
                                                                                id="actualCost"
                                                                                className="rc-tooltip-custom-class"
                                                                                backgroundColor="#007bff"
                                                                                place="left"
                                                                                effect="solid"
                                                                                delayShow={500}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </th>
                                                            </>
                                                        ))}
                                                        <th className="build-add set-value blue-bg-col gap-x">
                                                            <div className="d-flex col-md-12 per-value-outer">
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "softCostTotal", type: "settings" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex col-md-6 align-items-center">
                                                                    <div className="text-number">
                                                                        <NumberFormat
                                                                            displayType="text"
                                                                            value={getTotalCosts({ key: "softCostTotal", type: "actual" })}
                                                                            decimalScale={0}
                                                                            prefix={"$ "}
                                                                            thousandSeparator={true}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </th>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </LoadingOverlay>
    );
};
export default SoftCosts;
